// Takbeer Counter — Dhul Hijjah dhikr counter for the long takbeer formula.
//
// Recited especially during the first 13 days of Dhul Hijjah:
//   Takbeer al-Mutlaq    — any time, days 1–13.
//   Takbeer al-Muqayyad  — after every fard prayer, from Fajr 9th → Asr 13th.
//
// Counts persist per Hijri date, plus a lifetime tally. Tapping the ring
// counts one and triggers haptics. Resets per day (or full reset).

import { useState, useEffect, useRef, useCallback } from "react";
import { hapticTasbeeh } from "../haptics";

// ─────────────────────────────────────────────────────────────
// Audio source — streamed directly from archive.org
// Loops until user stops or auto-cap (2 hours) elapses.
// ─────────────────────────────────────────────────────────────
const TAKBEERAAT_AUDIO_URL = "https://ia800909.us.archive.org/35/items/takberat_______al3eed-------mp3/takbiratEidAl-adha.mp3";
const TAKBEERAAT_AUTO_STOP_SECONDS = 2 * 60 * 60;
const SHARE_MESSAGE = `*Takbeeraat in Your Home*

Please play and read along.

It is favourable to read the Takbeeraat constantly in the First 10 days of DhulHijjah.

*Your phone will play Takbeeraat for 2 hours without stopping even if your phone is locked and won't drain your battery.*

*Send to all your friends and family as a continuous Sadaqa-e-Jaariya.*

${TAKBEERAAT_AUDIO_URL}`;

function fmtClock(secs) {
  if (!Number.isFinite(secs) || secs < 0) secs = 0;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

const ARABIC_NUMS = ["٠","١","٢","٣","٤","٥","٦","٧","٨","٩"];
const toArabicNum = n => String(n).split("").map(c => ARABIC_NUMS[parseInt(c)] || c).join("");

const FORMULA = {
  arabic: "اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ، لَا إِلَٰهَ إِلَّا اللَّهُ، وَاللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ، وَلِلَّهِ الْحَمْدُ",
  translit: "Allāhu akbar · Allāhu akbar · Allāhu akbar · lā ilāha illa-llāh · wa-llāhu akbar · Allāhu akbar · wa lillāhi-l-ḥamd",
  meaning: "Allah is the Greatest, Allah is the Greatest, Allah is the Greatest. There is no god but Allah, and Allah is the Greatest, Allah is the Greatest, and to Allah belongs all praise.",
};

// Current Hijri date via Umm al-Qura calendar. Returns { day, month, year, monthEn }.
function getHijriToday() {
  try {
    const fmt = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { day: 'numeric', month: 'long', year: 'numeric' });
    const parts = fmt.formatToParts(new Date());
    const day = parseInt(parts.find(p => p.type === 'day').value, 10);
    const monthEn = parts.find(p => p.type === 'month').value;
    const year = parseInt(parts.find(p => p.type === 'year').value, 10);
    const monthIdxMap = { 'Muharram':1,'Safar':2,'Rabiʻ I':3,'Rabiʻ II':4,'Jumada I':5,'Jumada II':6,'Rajab':7,'Shaʻban':8,'Ramadan':9,'Shawwal':10,"Dhuʻl-Qiʻdah":11,"Dhuʻl-Hijjah":12 };
    const month = monthIdxMap[monthEn] ||
      (monthEn.toLowerCase().startsWith('dhu') && monthEn.toLowerCase().includes('h') ? 12 : 0);
    return { day, month, year, monthEn };
  } catch { return null; }
}

function hijriKey(h) { return h ? `${h.year}-${String(h.month).padStart(2,'0')}-${String(h.day).padStart(2,'0')}` : 'unknown'; }

export default function TakbeerCounter({ darkMode }) {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("takbeerLastTab") || "counter"; } catch { return "counter"; }
  });
  useEffect(() => { try { localStorage.setItem("takbeerLastTab", tab); } catch {} }, [tab]);

  const accentTop = "#ffb300";
  const muted = darkMode ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const tabBorder = darkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",minHeight:0}}>
      <div style={{display:"flex",gap:0,padding:"6px 18px 0",borderBottom:`1px solid ${tabBorder}`,flexShrink:0}}>
        {[
          { id: "counter", label: "Counter" },
          { id: "listen",  label: "Listen"  },
        ].map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex:1, padding:"12px 6px 11px",
              background:"transparent", border:"none", cursor:"pointer",
              fontFamily:'"JetBrains Mono",ui-monospace,monospace',
              fontSize:11, fontWeight:800, letterSpacing:"1.6px", textTransform:"uppercase",
              color: active ? accentTop : muted,
              borderBottom: `2px solid ${active ? accentTop : "transparent"}`,
              transition:"color 0.18s, border-color 0.18s",
            }}>{t.label}</button>
          );
        })}
      </div>
      <div style={{flex:1, overflowY:"auto", minHeight:0}}>
        {tab === "counter" && <CounterPanel darkMode={darkMode} />}
        {tab === "listen"  && <ListenPanel darkMode={darkMode} />}
      </div>
    </div>
  );
}

function CounterPanel({ darkMode }) {
  const [hijri] = useState(() => getHijriToday());
  const dayKey = hijriKey(hijri);

  // Per-day counts in a single object so older days remain visible later.
  const [counts, setCounts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("takbeerCounts") || "{}"); } catch { return {}; }
  });
  const [total, setTotal] = useState(() => parseInt(localStorage.getItem("takbeerTotal") || "0", 10));

  const dayCount = counts[dayKey] || 0;

  // Persist on change.
  useEffect(() => {
    try { localStorage.setItem("takbeerCounts", JSON.stringify(counts)); } catch {}
  }, [counts]);
  useEffect(() => {
    try { localStorage.setItem("takbeerTotal", String(total)); } catch {}
  }, [total]);

  const tap = () => {
    const next = (counts[dayKey] || 0) + 1;
    setCounts({ ...counts, [dayKey]: next });
    setTotal(t => t + 1);
    hapticTasbeeh(next);
  };

  const resetToday = () => {
    if (!window.confirm(`Reset today's Takbeer count? (${dayCount} so far)`)) return;
    setCounts({ ...counts, [dayKey]: 0 });
  };

  const isDhulHijjah = hijri && hijri.month === 12;
  const inWindow = isDhulHijjah && hijri.day >= 1 && hijri.day <= 13;
  const muqayyadActive = isDhulHijjah && hijri.day >= 9 && hijri.day <= 13;

  const accent  = "#ffb300";
  const glow    = "rgba(255,179,0,0.4)";
  const accent2 = "#cc8e00";

  // Ring geometry (matches Tasbeeh)
  const ringSize = Math.min(typeof window !== 'undefined' ? window.innerWidth - 60 : 320, 380);
  const rSvg = (ringSize - 48) / 2;

  const bgGradient = darkMode
    ? `radial-gradient(ellipse at 50% 30%, ${accent}12 0%, transparent 70%)`
    : `radial-gradient(ellipse at 50% 30%, ${accent}08 0%, transparent 70%)`;

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"6px 20px 20px",gap:0,textAlign:"center",position:"relative",overflow:"hidden",minHeight:"100%",background:bgGradient}}>
      {/* Hijri day badge + window state */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4,marginBottom:10}}>
        {hijri ? (
          <div style={{
            padding:'5px 12px', borderRadius:999,
            background: inWindow ? `${accent}1a` : darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            border:`1px solid ${inWindow ? accent + '55' : 'rgba(255,255,255,0.08)'}`,
            color: inWindow ? accent : 'var(--muted)',
            fontFamily:'"JetBrains Mono",ui-monospace,monospace',
            fontSize:10, fontWeight:800, letterSpacing:'1.6px', textTransform:'uppercase',
          }}>
            {hijri.day} {hijri.monthEn} {hijri.year}
          </div>
        ) : null}
        {muqayyadActive && (
          <div style={{
            padding:'5px 10px', borderRadius:999,
            background:'rgba(0,201,167,0.12)', border:'1px solid rgba(0,201,167,0.4)',
            color:'#00c9a7',
            fontFamily:'"JetBrains Mono",ui-monospace,monospace',
            fontSize:9, fontWeight:800, letterSpacing:'1.4px',
          }}>MUQAYYAD ACTIVE</div>
        )}
      </div>

      {/* Arabic formula */}
      <div style={{marginBottom:6,zIndex:1,maxWidth:520}}>
        <div style={{fontSize:"22px",fontWeight:800,color:accent,direction:"rtl",letterSpacing:"1px",lineHeight:1.7,fontFamily:"'Scheherazade New','Amiri',serif",marginBottom:6,textShadow:`0 0 26px ${glow}`}}>{FORMULA.arabic}</div>
        <div style={{fontSize:11,color:'var(--muted)',fontStyle:'italic',lineHeight:1.55,letterSpacing:'0.2px'}}>{FORMULA.translit}</div>
      </div>

      {/* Ring counter */}
      <div
        onClick={tap}
        style={{position:"relative",width:`${ringSize}px`,height:`${ringSize}px`,flexShrink:0,cursor:"pointer",WebkitTapHighlightColor:"transparent",userSelect:"none",zIndex:1,marginTop:8}}
      >
        <svg width={ringSize} height={ringSize} style={{position:"absolute",inset:0,overflow:"visible"}}>
          <circle cx={ringSize/2} cy={ringSize/2} r={rSvg} fill="none" stroke={darkMode?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"} strokeWidth="16"/>
          <circle cx={ringSize/2} cy={ringSize/2} r={rSvg} fill="none"
            stroke={accent} strokeWidth="16"
            strokeDasharray={`${Math.min(dayCount/100,1) * 2 * Math.PI * rSvg} ${2 * Math.PI * rSvg}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${ringSize/2} ${ringSize/2})`}
            style={{transition:"stroke-dasharray 0.2s ease"}}
          />
        </svg>
        <div style={{
          position:"absolute",inset:"24px",borderRadius:"50%",
          background:darkMode?`radial-gradient(circle at 40% 30%, ${accent}18, #07090c 75%)`:`radial-gradient(circle at 40% 30%, ${accent}10, #f5f5f5 75%)`,
          border:`1.5px solid ${accent}30`,
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
          boxShadow:`0 16px 50px rgba(0,0,0,${darkMode?0.5:0.15}), inset 0 1px 0 rgba(255,255,255,${darkMode?0.06:0.4})`,
        }}>
          <div style={{fontSize:"8.5px",fontWeight:800,color:"var(--muted)",letterSpacing:"3px",marginBottom:4,textTransform:"uppercase"}}>Today</div>
          <div style={{fontSize:"68px",fontWeight:900,color:accent,lineHeight:1,fontVariantNumeric:"tabular-nums",letterSpacing:"-3px"}}>{dayCount}</div>
          <div style={{fontSize:"20px",color:accent,opacity:0.5,fontFamily:"'Scheherazade New','Amiri',serif",direction:"rtl",marginTop:-2}}>{toArabicNum(dayCount)}</div>
        </div>
      </div>

      <div style={{fontSize:10,color:"var(--muted)",opacity:0.6,marginTop:6,marginBottom:10}}>Tap the circle to count one takbeer</div>

      {/* Stat row */}
      <div style={{display:"flex",gap:12,zIndex:1,marginBottom:14}}>
        {[
          { v: dayCount, label: "TODAY" },
          { v: total,    label: "LIFETIME" },
        ].map(({v,label}) => (
          <div key={label} style={{textAlign:"center",padding:"12px 22px",
            background:darkMode?`linear-gradient(135deg, ${accent}12, ${accent}05)`:`linear-gradient(135deg, ${accent}10, ${accent}03)`,
            border:`1px solid ${accent}20`, borderRadius:16, minWidth:95}}>
            <div style={{fontSize:24,fontWeight:800,color:accent}}>{v}</div>
            <div style={{fontSize:8,color:"var(--muted)",fontWeight:700,letterSpacing:"1.5px",marginTop:4}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Meaning */}
      <div style={{maxWidth:480, padding:'10px 14px', background:darkMode?'rgba(255,255,255,0.025)':'rgba(0,0,0,0.025)', border:`1px solid ${accent2}22`, borderRadius:12, fontSize:11.5, color:'var(--muted)', lineHeight:1.6, fontStyle:'italic'}}>
        {FORMULA.meaning}
      </div>

      {/* Reset */}
      <button onClick={resetToday} style={{marginTop:14,padding:"9px 28px",background:"transparent",border:`1px solid ${darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.1)"}`,borderRadius:20,color:"var(--muted)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",letterSpacing:"1px",textTransform:"uppercase",zIndex:1}}>Reset Today</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ListenPanel — Takbeeraat audio player (loops up to 2 hours,
// lock-screen controls via MediaSession, share with family)
// ═══════════════════════════════════════════════════════════════
function ListenPanel({ darkMode }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pos, setPos] = useState(0);
  const [duration, setDuration] = useState(0);
  const elapsedBaseRef = useRef(0);
  const stoppedByCapRef = useRef(false);

  const accent = "#ffb300";
  const glow   = "rgba(255,179,0,0.4)";
  const teal   = "#00c9a7";

  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const a = new Audio();
    // No crossOrigin — archive.org does not send CORS headers, and we only
    // need plain playback (no canvas/Analyser access). Setting crossOrigin
    // would block the load on WKWebView with a silent CORS failure.
    a.src = TAKBEERAAT_AUDIO_URL;
    a.preload = "auto";
    a.loop = false;
    a.playsInline = true;
    audioRef.current = a;

    const onMeta = () => { setDuration(a.duration || 0); setLoaded(true); setLoadError(null); };
    const onCanPlay = () => { setLoaded(true); setLoadError(null); };
    const onTime = () => {
      setPos(a.currentTime || 0);
      setElapsed(elapsedBaseRef.current + (a.currentTime || 0));
    };
    const onEnded = () => {
      const totalSoFar = elapsedBaseRef.current + (a.duration || 0);
      if (totalSoFar >= TAKBEERAAT_AUTO_STOP_SECONDS) {
        stoppedByCapRef.current = true;
        setPlaying(false);
        elapsedBaseRef.current = totalSoFar;
        setElapsed(totalSoFar);
        if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "none";
        return;
      }
      elapsedBaseRef.current = totalSoFar;
      a.currentTime = 0;
      a.play().catch(() => setPlaying(false));
    };
    const onPlay  = () => { setPlaying(true);  setLoadError(null); if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing"; };
    const onPause = () => { setPlaying(false); if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused"; };
    const onError = () => {
      setPlaying(false);
      const err = a.error;
      const msg = err
        ? ({ 1: "Playback cancelled", 2: "Network error — check connection", 3: "Audio decode error", 4: "Audio source not supported on this device" }[err.code] || `Audio error (${err.code})`)
        : "Could not load audio";
      setLoadError(msg);
    };

    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("canplay", onCanPlay);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnded);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("error", onError);
    return () => {
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("canplay", onCanPlay);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("error", onError);
      try { a.pause(); a.src = ""; } catch {}
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "none";
        try {
          navigator.mediaSession.setActionHandler("play", null);
          navigator.mediaSession.setActionHandler("pause", null);
          navigator.mediaSession.setActionHandler("stop", null);
        } catch {}
      }
    };
  }, []);

  const togglePlay = useCallback(async () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) {
      if (stoppedByCapRef.current) {
        elapsedBaseRef.current = 0; setElapsed(0); a.currentTime = 0;
        stoppedByCapRef.current = false;
      }
      // iOS requires the play() promise be created synchronously inside the
      // tap handler. If the audio hasn't loaded yet, .play() will trigger
      // the network fetch under the user-gesture context, which is what we
      // want — otherwise iOS blocks autoplay on the eventual canplay event.
      setLoadError(null);
      try {
        await a.play();
        if ("mediaSession" in navigator) {
          navigator.mediaSession.metadata = new window.MediaMetadata({
            title: "Takbeeraat",
            artist: "Sirat",
            album: "First 10 Days of Dhul Hijjah",
            artwork: [{ src: "/logo192.png", sizes: "192x192", type: "image/png" }],
          });
          navigator.mediaSession.setActionHandler("play",  () => { a.play().catch(()=>{}); });
          navigator.mediaSession.setActionHandler("pause", () => { a.pause(); });
          navigator.mediaSession.setActionHandler("stop",  () => { a.pause(); a.currentTime = 0; elapsedBaseRef.current = 0; setElapsed(0); });
        }
      } catch (e) {
        // Most common reasons: network down, source rejected, or autoplay
        // policy. The error event handler will set a user-facing message,
        // but cover the case where .play() rejects without an error event.
        if (!a.error) setLoadError(e && e.name === "NotAllowedError" ? "Tap play again to start" : "Could not start playback");
      }
    } else {
      a.pause();
    }
  }, []);

  const restart = useCallback(() => {
    const a = audioRef.current; if (!a) return;
    elapsedBaseRef.current = 0;
    setElapsed(0);
    stoppedByCapRef.current = false;
    a.currentTime = 0;
    a.play().catch(() => {});
  }, []);

  const handleShare = useCallback(async () => {
    const data = { title: "Takbeeraat in Your Home", text: SHARE_MESSAGE };
    try {
      if (navigator.share) { await navigator.share(data); return; }
    } catch (_) { return; }
    try { window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`, "_blank"); } catch {}
  }, []);

  const capRemaining = Math.max(0, TAKBEERAAT_AUTO_STOP_SECONDS - elapsed);
  const cappedReached = elapsed >= TAKBEERAAT_AUTO_STOP_SECONDS;
  const progressPct = duration > 0 ? (pos / duration) * 100 : 0;
  const cardBg = darkMode ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.025)";
  const cardBorder = darkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"18px 20px 28px",gap:18,minHeight:"100%",background:darkMode?`radial-gradient(ellipse at 50% 25%, ${accent}10 0%, transparent 70%)`:`radial-gradient(ellipse at 50% 25%, ${accent}08 0%, transparent 70%)`}}>
      <div style={{fontFamily:'"JetBrains Mono",ui-monospace,monospace',fontSize:9,fontWeight:800,letterSpacing:"2px",color:"var(--muted)",textTransform:"uppercase",marginTop:6}}>
        Takbeeraat — Continuous Play
      </div>

      <div style={{fontFamily:"'Scheherazade New','Amiri',serif",fontSize:24,color:accent,direction:"rtl",lineHeight:1.7,letterSpacing:"1px",textAlign:"center",maxWidth:520,textShadow:playing?`0 0 28px ${glow}, 0 0 12px ${glow}`:`0 0 14px ${glow}`,transition:"text-shadow 0.6s"}}>
        اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ
      </div>

      <button onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} style={{
        width:128, height:128, borderRadius:"50%",
        background: playing
          ? `radial-gradient(circle at 35% 30%, ${accent}, ${accent}aa 60%, ${accent}55)`
          : `radial-gradient(circle at 35% 30%, ${accent}88, ${accent}33 60%, transparent)`,
        border:`2px solid ${accent}`, cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow: playing ? `0 0 60px ${accent}55, 0 16px 50px rgba(0,0,0,${darkMode?0.55:0.2})` : `0 12px 36px rgba(0,0,0,${darkMode?0.45:0.15})`,
        transition:"box-shadow 0.3s, background 0.3s",
        color: darkMode ? "#0a0700" : "#1a1100",
      }}>
        {playing ? (
          <svg width="42" height="42" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
        ) : (
          <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        )}
      </button>

      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,minHeight:38}}>
        {loadError && (
          <div style={{fontSize:12,color:"#ff7766",letterSpacing:"0.5px",textAlign:"center",maxWidth:340,lineHeight:1.4}}>{loadError}</div>
        )}
        {!loaded && !cappedReached && !loadError && (
          <div style={{fontSize:11,color:"var(--muted)",letterSpacing:"1px",textTransform:"uppercase"}}>{playing ? "Buffering…" : "Tap play to begin"}</div>
        )}
        {loaded && !cappedReached && (
          <>
            <div style={{fontFamily:'"JetBrains Mono",ui-monospace,monospace',fontSize:14,fontWeight:700,color:playing?accent:"var(--text)",letterSpacing:"1px",fontVariantNumeric:"tabular-nums"}}>
              {fmtClock(elapsed)} <span style={{opacity:0.5}}>/ {fmtClock(TAKBEERAAT_AUTO_STOP_SECONDS)}</span>
            </div>
            <div style={{fontSize:10,color:"var(--muted)",letterSpacing:"1.4px",textTransform:"uppercase"}}>
              {playing ? `Playing — ${fmtClock(capRemaining)} remaining` : (elapsed > 0 ? "Paused" : "Tap to begin")}
            </div>
          </>
        )}
        {cappedReached && (
          <div style={{textAlign:"center"}}>
            <div style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontSize:18,color:accent}}>Alhamdulillah — 2 hours complete</div>
            <div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>Tap play to start again</div>
          </div>
        )}
      </div>

      <div style={{width:"100%",maxWidth:420,height:4,borderRadius:2,background:darkMode?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.07)",overflow:"hidden"}}>
        <div style={{height:"100%",width:`${progressPct}%`,background:`linear-gradient(90deg, ${accent}, ${accent}aa)`,transition:"width 0.3s linear"}} />
      </div>

      <div style={{display:"flex",gap:10,width:"100%",maxWidth:420}}>
        <button onClick={restart} disabled={!loaded} style={{
          flex:1, padding:"12px 8px", borderRadius:12, cursor: loaded ? "pointer" : "default",
          background:"transparent", border:`1px solid ${cardBorder}`,
          color:"var(--muted)", fontFamily:"inherit", fontSize:12, fontWeight:700, letterSpacing:"1px",
          opacity: loaded ? 1 : 0.5,
        }}>↺ Restart</button>
        <button onClick={handleShare} style={{
          flex:2, padding:"12px 8px", borderRadius:12, cursor:"pointer",
          background:`linear-gradient(135deg, ${teal}cc, ${teal}88)`,
          border:`1px solid ${teal}66`,
          color:"#001a14", fontFamily:'"JetBrains Mono",ui-monospace,monospace',
          fontSize:12, fontWeight:800, letterSpacing:"1px",
        }}>↗ Share with Family</button>
      </div>

      <div style={{maxWidth:480,padding:"14px 16px",background:cardBg,border:`1px solid ${cardBorder}`,borderRadius:14,fontSize:12.5,color:"var(--text)",lineHeight:1.6}}>
        <div style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontSize:17,color:accent,marginBottom:6}}>Takbeeraat in Your Home</div>
        <p style={{margin:"0 0 8px"}}>It is favourable to read the Takbeeraat constantly in the first 10 days of Dhul Hijjah.</p>
        <p style={{margin:"0 0 8px",color:"var(--muted)"}}>Your phone will play for up to 2 hours without stopping, even while locked — playback uses standard streaming and won't drain your battery.</p>
        <p style={{margin:0,color:"var(--muted)",fontStyle:"italic"}}>Share with family as a Sadaqa-e-Jaariya.</p>
      </div>
    </div>
  );
}
